/**
 * Should the one-time onboarding wizard be held back because a build / repair /
 * publish pipeline is in flight?
 *
 * QA: "Onboarding quick setup modal appears during active build and blocks
 * workspace". The wizard renders as a full-screen takeover on the project page,
 * so opening it over live work replaces the whole builder — chat, preview,
 * terminal and the build progress pane all disappear. The build itself survives
 * (the page component does not unmount and the SSE reader carries no abort
 * signal), but the user is left staring at a survey with no way back.
 *
 * WHY THIS IS NOT `isBuildBusy` FROM lib/build-busy.ts
 *
 * That predicate answers a different question — "may a follow-up prompt be sent
 * right now, or must it join the queue?" — and two of its terms are actively
 * wrong here:
 *
 *   • `sending` MUST be excluded. The chat call site holds its send lock across
 *     the whole of launchBuildFlow, so `sending` is true there BY CONSTRUCTION.
 *     Including it would suppress the wizard on every chat-originated first
 *     build — that is the wizard's own intended entry point, so the feature
 *     would simply never appear.
 *   • `publishing` MUST be included, and isBuildBusy has no deploy term at all.
 *     QA's scope names publish explicitly.
 *
 * A separate module rather than a new field on isBuildBusy: adding `deploying`
 * there would silently change queue-drain behaviour, which is a different
 * ticket.
 *
 * WHAT MUST NEVER BE A SIGNAL HERE
 *
 * Two tempting "a build is happening" flags are traps, because both are true in
 * situations where the wizard SHOULD still open. Neither appears in
 * OnboardingBuildSignals, and the tests pin their absence:
 *
 *   • an in-flight send (see `sending` above)
 *   • "a build was claimed at some point" (hasStartedRef-style). That flag gets
 *     set with no build running and no files written — by the intent-gate
 *     clarification path and the out-of-scope decline path. Those users' next
 *     message would find it already true and lose onboarding permanently.
 *
 * WHY NO "IS THIS THE FIRST BUILD?" HEURISTIC IS NEEDED
 *
 * At both intended entry points every real phase signal is already false by
 * construction — the chat path is fenced behind an empty file list and its own
 * busy check, the URL path behind "no build has started". So "any phase is
 * running" is, on its own, an exact discriminator between "about to start the
 * first build" (open the wizard) and "something else is already running"
 * (hold it back).
 */

export interface OnboardingBuildSignals {
  /** AI plan / code generation in flight. */
  generating: boolean;
  /** Preview (re)mount or dev-server (re)start. */
  booting: boolean;
  /** Writing generated files into the sandbox. */
  applying: boolean;
  /** The autofix loop repairing a compile error. */
  fixing: boolean;
  /**
   * The escalation repair. Tracked separately because it sets the `isFixing`
   * state but NOT the autofix ref, so a ref-only read of the fix flag is blind
   * to it.
   */
  escalating: boolean;
  /** A deploy / publish is running. */
  publishing: boolean;
}

/**
 * Machine-readable cause, reported as `suppressed_reason` on the
 * `onboarding_suppressed` event so the funnel can tell WHY a user was skipped
 * rather than only that they were.
 */
export type OnboardingDeferReason =
  | "build_generating"
  | "build_booting"
  | "build_applying"
  | "build_repairing"
  | "publishing";

/**
 * `null` means idle — safe to open the wizard. Any other value is the reason to
 * hold it back.
 *
 * Deferring is NOT dismissing: the caller must leave `onboarding_shown_at`
 * unwritten, or the user is permanently opted out of a wizard they never saw.
 */
export function onboardingDeferReason(
  signals: OnboardingBuildSignals,
): OnboardingDeferReason | null {
  if (signals.generating) return "build_generating";
  if (signals.booting) return "build_booting";
  if (signals.applying) return "build_applying";
  if (signals.fixing || signals.escalating) return "build_repairing";
  if (signals.publishing) return "publishing";
  return null;
}

/** Convenience for call sites that only need the boolean. */
export function shouldDeferOnboarding(signals: OnboardingBuildSignals): boolean {
  return onboardingDeferReason(signals) !== null;
}
